import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { MoveUpRight, Square, Type, Undo2, Trash2 } from "lucide-react";

const COLORS = ["#E11D48", "#F97316", "#FACC15", "#2563EB"];
const MAX_W = 2400;

function labelAnchor(s, fs) {
  if (s.type === "text") return { x: s.x1, y: s.y1, align: "start" };
  if (s.type === "box") return { x: Math.min(s.x1, s.x2), y: Math.min(s.y1, s.y2) - fs * 1.9, align: "start" };
  const ang = Math.atan2(s.y2 - s.y1, s.x2 - s.x1);
  return { x: s.x1 - Math.cos(ang) * fs * 1.2, y: s.y1 - Math.sin(ang) * fs * 1.2 - fs * 0.85, align: "center" };
}

function drawLabel(ctx, s, fs) {
  ctx.font = `600 ${fs}px "IBM Plex Sans", system-ui, sans-serif`;
  const pad = fs * 0.4, w = ctx.measureText(s.label).width + pad * 2, h = fs * 1.6;
  const a = labelAnchor(s, fs);
  let x = a.align === "center" ? a.x - w / 2 : a.x;
  let y = a.y;
  x = Math.max(2, Math.min(x, ctx.canvas.width - w - 2));
  y = Math.max(2, Math.min(y, ctx.canvas.height - h - 2));
  ctx.fillStyle = s.color;
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(x, y, w, h, fs * 0.3); else ctx.rect(x, y, w, h);
  ctx.fill();
  ctx.fillStyle = s.color === "#FACC15" ? "#111827" : "#FFFFFF";
  ctx.textBaseline = "middle";
  ctx.fillText(s.label, x + pad, y + h / 2);
}

function drawShape(ctx, s, lw) {
  ctx.strokeStyle = s.color; ctx.fillStyle = s.color; ctx.lineWidth = lw; ctx.lineCap = "round"; ctx.lineJoin = "round";
  if (s.type === "box") ctx.strokeRect(s.x1, s.y1, s.x2 - s.x1, s.y2 - s.y1);
  if (s.type === "arrow") {
    const ang = Math.atan2(s.y2 - s.y1, s.x2 - s.x1), head = lw * 4.5;
    ctx.beginPath(); ctx.moveTo(s.x1, s.y1); ctx.lineTo(s.x2 - Math.cos(ang) * head * 0.6, s.y2 - Math.sin(ang) * head * 0.6); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s.x2, s.y2);
    ctx.lineTo(s.x2 - head * Math.cos(ang - Math.PI / 7), s.y2 - head * Math.sin(ang - Math.PI / 7));
    ctx.lineTo(s.x2 - head * Math.cos(ang + Math.PI / 7), s.y2 - head * Math.sin(ang + Math.PI / 7));
    ctx.closePath(); ctx.fill();
  }
  if (s.label) drawLabel(ctx, s, Math.max(14, lw * 5));
}

export default function ScreenshotMarkup({ file, onDone, onSkip, onCancel }) {
  const canvasRef = useRef();
  const imgRef = useRef(null);
  const [shapes, setShapes] = useState([]);
  const [draft, setDraft] = useState(null);
  const [labeling, setLabeling] = useState(null);
  const [tool, setTool] = useState("arrow");
  const [color, setColor] = useState(COLORS[0]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setShapes([]); setLabeling(null); setReady(false); imgRef.current = null;
    if (!file) { return undefined; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_W / img.naturalWidth);
      const c = canvasRef.current;
      if (c) { c.width = Math.round(img.naturalWidth * scale); c.height = Math.round(img.naturalHeight * scale); }
      imgRef.current = img; setReady(true);
    };
    img.src = url;
    return () => { URL.revokeObjectURL(url); };
  }, [file]);

  useEffect(() => {
    const c = canvasRef.current, img = imgRef.current;
    if (!c || !img) { return; }
    const ctx = c.getContext("2d");
    ctx.drawImage(img, 0, 0, c.width, c.height);
    const lw = Math.max(3, c.width / 280);
    [...shapes, ...(draft ? [draft] : [])].forEach(s => drawShape(ctx, s, lw));
  }, [shapes, draft, ready]);

  const pt = (e) => {
    const c = canvasRef.current, r = c.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * c.width, y: ((e.clientY - r.top) / r.height) * c.height };
  };
  const commitLabel = () => {
    if (!labeling) return;
    const text = labeling.value.trim();
    if (text) {
      if (labeling.idx === null) setShapes(s => [...s, { type: "text", color, x1: labeling.x, y1: labeling.y, x2: labeling.x, y2: labeling.y, label: text }]);
      else setShapes(s => s.map((sh, i) => i === labeling.idx ? { ...sh, label: text } : sh));
    }
    setLabeling(null);
  };
  const down = (e) => {
    e.preventDefault();
    if (labeling) { commitLabel(); return; }
    const p = pt(e);
    if (tool === "text") { setLabeling({ idx: null, x: p.x, y: p.y, value: "" }); return; }
    e.currentTarget.setPointerCapture(e.pointerId);
    setDraft({ type: tool, color, x1: p.x, y1: p.y, x2: p.x, y2: p.y });
  };
  const move = (e) => { if (draft) { const p = pt(e); setDraft({ ...draft, x2: p.x, y2: p.y }); } };
  const up = () => {
    if (draft && Math.hypot(draft.x2 - draft.x1, draft.y2 - draft.y1) > 6) {
      const c = canvasRef.current, fs = Math.max(14, Math.max(3, c.width / 280) * 5);
      const a = labelAnchor(draft, fs);
      setLabeling({ idx: shapes.length, x: a.x, y: a.y, value: "" });
      setShapes(s => [...s, draft]);
    }
    setDraft(null);
  };

  const finish = () => {
    const c = canvasRef.current, ctx = c.getContext("2d");
    const text = labeling?.value.trim();
    let list = shapes;
    if (text && labeling.idx === null) list = [...shapes, { type: "text", color, x1: labeling.x, y1: labeling.y, x2: labeling.x, y2: labeling.y, label: text }];
    else if (text) list = shapes.map((sh, i) => i === labeling.idx ? { ...sh, label: text } : sh);
    ctx.drawImage(imgRef.current, 0, 0, c.width, c.height);
    list.forEach(s => drawShape(ctx, s, Math.max(3, c.width / 280)));
    c.toBlob((blob) => {
      const base = (file.name || "screenshot").replace(/\.[^.]+$/, "");
      onDone(new File([blob], `${base}-marked.png`, { type: "image/png" }));
    }, "image/png");
  };

  return (
    <Dialog open={!!file} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="bg-white border-neutral-200 max-w-5xl w-[96vw] max-h-[94vh] overflow-y-auto p-4" data-testid="markup-dialog"
        onEscapeKeyDown={(e) => { if (labeling) { e.preventDefault(); setLabeling(null); } }}>
        <DialogHeader>
          <DialogTitle>Mark up screenshot</DialogTitle>
          <DialogDescription className="text-xs">Drag to draw arrows or boxes, then type an optional label. Use Text to place a label anywhere.</DialogDescription>
        </DialogHeader>
        <MarkupToolbar tool={tool} setTool={setTool} color={color} setColor={setColor}
          canUndo={shapes.length > 0} onUndo={() => { setLabeling(null); setShapes(s => s.slice(0, -1)); }} onClear={() => { setLabeling(null); setShapes([]); }} />
        <div className="rounded-md border border-neutral-200 bg-neutral-100 grid place-items-center overflow-hidden">
          <div className="relative inline-block max-w-full">
            <canvas ref={canvasRef} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
              className={`block max-w-full max-h-[48vh] sm:max-h-[62vh] w-auto h-auto touch-none select-none ${tool === "text" ? "cursor-text" : "cursor-crosshair"}`} data-testid="markup-canvas" />
            {labeling && <LabelInput labeling={labeling} canvas={canvasRef.current} color={color}
              onChange={(value) => setLabeling(l => ({ ...l, value }))} onCommit={commitLabel} onCancel={() => setLabeling(null)} />}
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={onCancel} data-testid="markup-cancel">Discard</Button>
          <Button variant="outline" onClick={() => onSkip(file)} data-testid="markup-skip">Attach without markup</Button>
          <Button onMouseDown={(e) => e.preventDefault()} onClick={finish} disabled={!ready} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="markup-attach">
            Attach{shapes.length ? ` with ${shapes.length} mark${shapes.length > 1 ? "s" : ""}` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LabelInput({ labeling, canvas, color, onChange, onCommit, onCancel }) {
  const inputRef = useRef();
  useEffect(() => { const t = setTimeout(() => inputRef.current?.focus(), 0); return () => { clearTimeout(t); }; }, []);
  const r = canvas.getBoundingClientRect();
  const sx = r.width / canvas.width, sy = r.height / canvas.height;
  const left = Math.max(0, Math.min(labeling.x * sx, r.width - 180));
  const top = Math.max(0, Math.min(labeling.y * sy, r.height - 36));
  return (
    <input ref={inputRef} autoFocus maxLength={60} value={labeling.value} placeholder={labeling.idx === null ? "Type a label…" : "Label (optional), Enter to add"}
      onChange={(e) => onChange(e.target.value)} onBlur={onCommit}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onCommit(); } if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onCancel(); } }}
      onPointerDown={(e) => e.stopPropagation()}
      className="absolute w-[180px] h-8 px-2 text-xs rounded-md bg-white shadow-lg outline-none border-2"
      style={{ left, top, borderColor: color }} data-testid="markup-label-input" />
  );
}

function MarkupToolbar({ tool, setTool, color, setColor, canUndo, onUndo, onClear }) {
  const toolBtn = (key, Icon, label) => (
    <button type="button" onClick={() => setTool(key)} data-testid={`markup-tool-${key}`}
      className={`h-8 px-3 rounded-full border text-xs font-medium flex items-center gap-1.5 transition-colors ${tool === key ? "bg-orange-100 border-orange-300 text-orange-700" : "bg-white border-neutral-200 text-neutral-700 hover:border-neutral-300"}`}>
      <Icon className="w-3.5 h-3.5" />{label}
    </button>
  );
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {toolBtn("arrow", MoveUpRight, "Arrow")}
      {toolBtn("box", Square, "Box")}
      {toolBtn("text", Type, "Text")}
      <span className="w-px h-6 bg-neutral-200 mx-1" />
      {COLORS.map(c => (
        <button key={c} type="button" onClick={() => setColor(c)} aria-label={`Colour ${c}`} data-testid={`markup-color-${c.slice(1)}`}
          className={`w-6 h-6 rounded-full border-2 transition-transform ${color === c ? "border-neutral-800 scale-110" : "border-white ring-1 ring-neutral-200"}`} style={{ background: c }} />
      ))}
      <span className="w-px h-6 bg-neutral-200 mx-1" />
      <Button type="button" size="sm" variant="ghost" onClick={onUndo} disabled={!canUndo} data-testid="markup-undo"><Undo2 className="w-3.5 h-3.5 mr-1" />Undo</Button>
      <Button type="button" size="sm" variant="ghost" onClick={onClear} disabled={!canUndo} data-testid="markup-clear"><Trash2 className="w-3.5 h-3.5 mr-1" />Clear</Button>
    </div>
  );
}
